import { ASSET_TYPES } from '$lib/constants/assetTypes';
import { ENVIO_API_TOKEN } from '$lib/constants/envioEndpoint';
import { MONERIUM_REMAPPINGS } from '$lib/constants/tokenAddresses';
import { stateEnvioEndpoint } from '$states/envioEndpoint.svelte';
import { type Address, formatUnits } from 'viem';

const payOwnersQuery = `
  query payOwners($address: String) {
    Metri_Pay_DelayModule(where: {owners: {ownerAddress: {_eq: $address}} }) {
      safeAddress
      owners(where: {ownerAddress: {_eq: $address}}) {
        isOG
      }
    }
  }
`;

export const getOwnedPayAccounts = async (metriAddress: Address): Promise<Address[]> => {
  const response = await fetch(stateEnvioEndpoint.current.https, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ENVIO_API_TOKEN}`,
    },
    body: JSON.stringify({
      query: payOwnersQuery,
      variables: { address: metriAddress },
      operationName: 'payOwners',
    }),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payDelayModules = ((await response.json()) as any)?.data?.Metri_Pay_DelayModule;

  return (payDelayModules ?? []).map(
    (delayModule: { safeAddress: Address }) => delayModule?.safeAddress,
  );
};

const delayModuleQuery = `
  query delayModuleBySafe($safeAddress: String) {
    Metri_Pay_DelayModule(where: {safeAddress: {_eq: $safeAddress}}) {
      id
    }
  }
`;

const delayModuleCache: { [key: string]: Address | null } = {};

export const clearDelayModuleCache = (): void => {
  for (const key in delayModuleCache) {
    delete delayModuleCache[key];
  }
};

export const getDelayModuleAddress = async (paySafeAddress: Address): Promise<Address | null> => {
  const cached = delayModuleCache[paySafeAddress];
  if (cached !== undefined) return cached;

  const response = await fetch(stateEnvioEndpoint.current.https, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ENVIO_API_TOKEN}`,
    },
    body: JSON.stringify({
      query: delayModuleQuery,
      variables: { safeAddress: paySafeAddress },
      operationName: 'delayModuleBySafe',
    }),
  });

  const result = await response.json();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const delayModule = (result as any)?.data?.Metri_Pay_DelayModule?.[0];

  const delayModuleAddress = (delayModule?.id as Address) ?? null;
  delayModuleCache[paySafeAddress] = delayModuleAddress;

  return delayModuleAddress;
};

const isMetriPayOwnerCache: { [key: string]: boolean } = {};

export const isMetriPayOwner = async (metriAddress: Address, payAddress: Address) => {
  const cacheKey = metriAddress + payAddress;
  const cached = isMetriPayOwnerCache[cacheKey];
  if (cached !== undefined) return cached;

  const answer = (await getOwnedPayAccounts(metriAddress)).includes(payAddress);

  isMetriPayOwnerCache[cacheKey] = answer;

  return answer;
};

const allowanceQuery = `
  query allowance($address: String) {
    Metri_Pay_RolesModule(where: {safeAddress: {_eq: $address}}) {
      tokenAddress
      spendingLimit
      lastSpendable
      lastSpentAt
    }
  }
`;

export const getAllowance = async (payAddress: Address) => {
  const response = await fetch(stateEnvioEndpoint.current.https, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${ENVIO_API_TOKEN}`,
    },
    body: JSON.stringify({
      query: allowanceQuery,
      variables: { address: payAddress },
      operationName: 'allowance',
    }),
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allowance = ((await response.json()) as any).data.Metri_Pay_RolesModule[0];

  if (!allowance) return undefined;

  const tokenAddress = MONERIUM_REMAPPINGS[allowance.tokenAddress] ?? allowance.tokenAddress;

  const asset = ASSET_TYPES.find((asset) => asset.contractAddress === tokenAddress);

  if (!asset) return undefined;

  const isLastSpentAtOlderThenPeriod = Date.now() - allowance.lastSpentAt * 1000 > 86400000;

  return {
    spendingLimit: formatUnits(allowance.spendingLimit, asset.decimals),
    spendableToday: formatUnits(
      isLastSpentAtOlderThenPeriod ? allowance.spendingLimit : allowance.lastSpendable,
      asset.decimals,
    ),
  };
};
